/**
 * Die eine IndexedDB dieser Anwendung -- und die eine Stelle, die sie öffnet.
 *
 * **Warum das eine eigene Datei ist.** Der Ausgangskorb öffnete `travel-
 * broadcast` bis hierher selbst, mit Version 1. Stellt man einen zweiten
 * Speicher daneben und öffnet ihn mit Version 2, dann scheitert der
 * Ausgangskorb danach mit `VersionError`: der Browser lehnt eine **kleinere**
 * Version rundheraus ab, und zwar auf demselben Gerät, auf dem gerade
 * ungesendete Nachrichten liegen. Eine Datenbank hat eine Version, nicht je
 * Speicher eine -- also gehört das Öffnen an eine Stelle.
 *
 * Wer einen dritten Speicher braucht, hebt `VERSION` und legt ihn in
 * `aufwerten()` an. Die Aufwertung läuft auf jedem Gerät genau einmal und darf
 * deshalb nichts voraussetzen: sie prüft jeden Speicher einzeln, statt sich auf
 * die alte Version zu verlassen. Vorhandene Inhalte überstehen sie -- genau
 * dafür gibt es sie.
 */

import { WORTLAUT } from '../texte'

const DB = 'travel-broadcast'

/**
 * 1 war der Ausgangskorb allein, 2 kam der Vorrat dazu (#13).
 */
const VERSION = 2

/** Was ohne Netz geschrieben wurde und noch raus muss. */
export const AUSGANG = 'ausgang'

/** Der zuletzt gelesene Stand, damit die App ohne Netz etwas zu zeigen hat. */
export const VORRAT = 'vorrat'

let offen: Promise<IDBDatabase> | null = null

function aufwerten(datenbank: IDBDatabase): void {
  if (!datenbank.objectStoreNames.contains(AUSGANG)) {
    datenbank.createObjectStore(AUSGANG, { keyPath: 'key', autoIncrement: true })
  }
  if (!datenbank.objectStoreNames.contains(VORRAT)) {
    // Ein fester Schlüssel je Eintrag, kein `autoIncrement`: im Vorrat steht
    // immer genau ein Satz, und der wird überschrieben statt angehäuft.
    datenbank.createObjectStore(VORRAT)
  }
}

export function db(): Promise<IDBDatabase> {
  if (offen !== null) return offen

  offen = new Promise<IDBDatabase>((resolve, reject) => {
    const anfrage = indexedDB.open(DB, VERSION)
    anfrage.onupgradeneeded = () => aufwerten(anfrage.result)
    anfrage.onsuccess = () => resolve(anfrage.result)
    anfrage.onerror = () => reject(anfrage.error ?? new Error('IndexedDB'))
    // **Ein zweiter Tab kann die Aufwertung aufhalten.** Er hält die alte
    // Version noch offen; ohne diesen Zweig hinge das Versprechen für immer,
    // und mit ihm hängt bloß der Vorrat -- die Anwendung läuft weiter.
    anfrage.onblocked = () => reject(new Error(WORTLAUT.technik.fehler.indexeddbBelegt))
  })

  // Einmal darum bitten, dass iOS diesen Speicher nicht wegräumt. Safari gibt
  // Website-Daten nach längerer Nichtnutzung frei; für eine App auf dem
  // Home-Bildschirm ist das entschärft, aber fragen kostet nichts.
  void navigator.storage?.persist?.().catch(() => undefined)

  return offen
}

/**
 * Die gemerkte Verbindung wegwerfen -- die naechste Arbeit oeffnet neu.
 *
 * **Weil ein Neustart der App hilft und sonst nichts** (1.41.0). Aus dem
 * Betrieb, mit Bildschirmfoto belegt: ein wartendes Foto liess sich nicht mehr
 * lesen („Ein wartendes Foto laesst sich auf diesem Geraet nicht mehr lesen"),
 * und **derselbe Eintrag** ging nach einem Neustart anstandslos hinaus. Der
 * Unterschied zwischen beiden Laeufen ist genau diese Verbindung: WebKit gibt
 * die Datenquelle hinter einem Blob irgendwann frei, und was an einer alten
 * Verbindung haengt, ist dann leer -- frisch geoeffnet kommt es wieder.
 *
 * Wer das ruft, hat einen Lesefehler in der Hand und versucht es noch einmal;
 * ein `close()` waehrend einer laufenden Transaktion wartet, bis sie durch
 * ist, und schadet deshalb nichts.
 */
export function verbindungWegwerfen(): void {
  const alt = offen
  offen = null
  void alt?.then((datenbank) => datenbank.close()).catch(() => undefined)
}

/**
 * Change one stored record in place -- read and write in **one**
 * transaction (1.78.0).
 *
 * The caller hands a function from the record as it is stored to the record
 * as it should be stored, or `undefined` to leave it. A record that is gone
 * (discarded meanwhile) stays gone: nothing is written. Resolves with
 * whether something was written, once the transaction is complete -- not
 * on the request's success, which comes before the data is safe.
 */
export function aendereIn<T>(
  store: string,
  key: IDBValidKey,
  aendern: (alt: T) => T | undefined,
): Promise<boolean> {
  return db().then(
    (datenbank) =>
      new Promise<boolean>((resolve, reject) => {
        const t = datenbank.transaction(store, 'readwrite')
        const s = t.objectStore(store)
        let geschrieben = false
        const lesen = s.get(key)
        lesen.onsuccess = () => {
          const alt = lesen.result as T | undefined
          if (alt === undefined) return
          const neu = aendern(alt)
          if (neu === undefined) return
          s.put(neu)
          geschrieben = true
        }
        t.oncomplete = () => resolve(geschrieben)
        t.onerror = () => reject(t.error ?? new Error('IndexedDB'))
        t.onabort = () => reject(t.error ?? new Error('IndexedDB'))
      }),
  )
}

/** Eine Arbeit auf **einem** Speicher, mit Versprechen statt Ereignissen. */
export function lauf<T>(
  store: string,
  modus: IDBTransactionMode,
  arbeit: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return db().then(
    (datenbank) =>
      new Promise<T>((resolve, reject) => {
        const t = datenbank.transaction(store, modus)
        const anfrage = arbeit(t.objectStore(store))
        anfrage.onsuccess = () => resolve(anfrage.result)
        anfrage.onerror = () => reject(anfrage.error ?? new Error('IndexedDB'))
      }),
  )
}
